/**
 * UsageCard - Display current usage metrics with beautiful gradient design
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Users, FolderKanban, Database, Mic, Zap, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { UsageSummary } from '@neokanban/types';

interface UsageCardProps {
  usage: UsageSummary;
}

interface UsageItemProps {
  label: string;
  current: number;
  limit: number;
  unit?: string;
  icon: React.ReactNode;
  gradientFrom: string;
  gradientTo: string;
}

const UsageItem: React.FC<UsageItemProps> = ({
  label,
  current,
  limit,
  unit = '',
  icon,
  gradientFrom,
  gradientTo
}) => {
  const { t } = useTranslation();
  const percentage = limit === -1 ? 0 : limit > 0 ? Math.round((current / limit) * 100) : 0;
  const isUnlimited = limit === -1;
  const isNearLimit = percentage >= 80;
  const isAtLimit = percentage >= 100;

  // Clamp percentage at 100 for visual display
  const displayPercentage = Math.min(percentage, 100);

  return (
    <div
      className="group relative p-5 rounded-2xl bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5"
      style={{
        animation: 'fadeIn 0.5s ease-out forwards',
      }}
    >
      {/* Gradient accent bar */}
      <div
        className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl opacity-80"
        style={{
          background: `linear-gradient(90deg, ${gradientFrom}, ${gradientTo})`,
        }}
      />

      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className="p-2.5 rounded-xl shadow-sm"
            style={{
              background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})`,
            }}
          >
            <div className="text-white">
              {icon}
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-gray-100">{label}</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {isUnlimited ? t('settings.subscription.unlimited') : t('settings.subscription.limitSuffix', { limit: limit.toLocaleString(), unit })}
            </p>
          </div>
        </div>

        {!isUnlimited && (
          <div className="text-right">
            <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
              {displayPercentage}%
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {t('settings.subscription.usedSuffix', { current: current.toLocaleString(), unit })}
            </div>
          </div>
        )}
      </div>

      {!isUnlimited && (
        <div className="space-y-2">
          {/* Custom gradient progress bar */}
          <div className="relative h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden shadow-inner">
            <div
              className="absolute top-0 left-0 h-full rounded-full transition-all duration-700 ease-out shadow-sm"
              style={{
                width: `${displayPercentage}%`,
                background: isAtLimit
                  ? 'linear-gradient(90deg, #ef4444, #dc2626)'
                  : isNearLimit
                    ? 'linear-gradient(90deg, #f59e0b, #d97706)'
                    : `linear-gradient(90deg, ${gradientFrom}, ${gradientTo})`,
              }}
            >
              <div className="absolute inset-0 opacity-30 animate-pulse"
                style={{
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                  animation: 'shimmer 2s infinite',
                }}
              />
            </div>
          </div>

          {isAtLimit && (
            <div className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400 font-medium">
              <TrendingUp className="w-3.5 h-3.5" />
              {t('settings.subscription.limitReached')}
            </div>
          )}
        </div>
      )}

      {isUnlimited && (
        <div className="flex items-center gap-2 px-3 py-2 bg-primary-50 dark:bg-primary-950/30 rounded-lg">
          <div className="text-2xl">∞</div>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('settings.subscription.unlimitedUsage')}</span>
        </div>
      )}
    </div>
  );
};

export const UsageCard: React.FC<UsageCardProps> = ({ usage }) => {
  const { t } = useTranslation();
  return (
    <Card className="usage-card-container border-gray-200 dark:border-gray-800 shadow-md">
      <CardHeader className="pb-4">
        <CardTitle className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
          {t('settings.subscription.usageLimits')}
        </CardTitle>
        <CardDescription className="text-base">
          {t('settings.subscription.usageDescription')}
        </CardDescription>
      </CardHeader>

      <CardContent className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <UsageItem
          label={t('settings.subscription.teamMembers')}
          current={usage.members.current}
          limit={usage.members.limit}
          icon={<Users className="w-5 h-5" />}
          gradientFrom="#3b82f6"
          gradientTo="#2563eb"
        />

        <UsageItem
          label={t('settings.subscription.projects')}
          current={usage.projects.current}
          limit={usage.projects.limit}
          icon={<FolderKanban className="w-5 h-5" />}
          gradientFrom="#8b5cf6"
          gradientTo="#7c3aed"
        />

        <UsageItem
          label={t('settings.subscription.storage')}
          current={usage.storageMb.current}
          limit={usage.storageMb.limit}
          unit="MB"
          icon={<Database className="w-5 h-5" />}
          gradientFrom="#06b6d4"
          gradientTo="#0891b2"
        />

        <UsageItem
          label={t('settings.subscription.transcriptionMinutes')}
          current={usage.transcriptionMinutes.current}
          limit={usage.transcriptionMinutes.limit}
          unit=" mins"
          icon={<Mic className="w-5 h-5" />}
          gradientFrom="#10b981"
          gradientTo="#059669"
        />

        <UsageItem
          label={t('settings.subscription.aiCredits')}
          current={usage.extractionCredits.current}
          limit={usage.extractionCredits.limit}
          icon={<Zap className="w-5 h-5" />}
          gradientFrom="#f59e0b"
          gradientTo="#d97706"
        />
      </CardContent>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        .usage-card-container .group:nth-child(1) { animation-delay: 0.1s; opacity: 0; }
        .usage-card-container .group:nth-child(2) { animation-delay: 0.2s; opacity: 0; }
        .usage-card-container .group:nth-child(3) { animation-delay: 0.3s; opacity: 0; }
        .usage-card-container .group:nth-child(4) { animation-delay: 0.4s; opacity: 0; }
        .usage-card-container .group:nth-child(5) { animation-delay: 0.5s; opacity: 0; }
      `}</style>
    </Card>
  );
};
